import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { GetRiskAssessmentResultConfigAuditByIdResponseRow } from '@risksmart-app/trpc/types';
import type { GetRiskAssessmentResultConfigAuditByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useIsFeatureVisibleToOrg } from 'src/utils/featureFlags';
import { useTRPC } from 'src/utils/trpc';

const mapTrpcRiskAssessmentResultConfigAuditToGraphQL = (
  trpcData: GetRiskAssessmentResultConfigAuditByIdResponseRow[] | undefined
): GetRiskAssessmentResultConfigAuditByIdQuery => {
  return {
    risk_assessment_result_config_audit: trpcData || [],
  };
};

export const useGetRiskAssessmentResultConfigAuditByIdTRPC = (
  id?: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  if (!id && !skip) {
    throw new Error(
      'Risk Assessment Result Config ID is required unless skip is true'
    );
  }

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.riskAssessmentResultConfigAudit.getById.queryOptions({
      id: id!,
    }),

    enabled: trpcEnabled && !skip,
  });

  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  const data = mapTrpcRiskAssessmentResultConfigAuditToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
